/**
 * Legal and finance queries and mutations.
 *
 * The save seeds the cache from its own response rather than invalidating,
 * following the pattern the other entities use: the response carries the
 * record's new version AND its recomputed readiness, so a refetch racing it
 * could hand the form a stale version and an out-of-date missing-field list.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.9, PRD §5.11
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { LegalFinance, SaveLegalFinanceRequest } from '@nexus/contracts';

import * as legalFinanceApi from './legal-finance.api';
import { queryKeys } from './query-keys';

export function useLegalFinance(startupId: string): UseQueryResult<LegalFinance> {
  return useQuery({
    queryKey: queryKeys.legalFinance.detail(startupId),
    queryFn: () => legalFinanceApi.getLegalFinance(startupId),
  });
}

export function useSaveLegalFinance(
  startupId: string,
): UseMutationResult<LegalFinance, Error, SaveLegalFinanceRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SaveLegalFinanceRequest) =>
      legalFinanceApi.saveLegalFinance(startupId, body),
    onSuccess: (record) => {
      queryClient.setQueryData(queryKeys.legalFinance.detail(startupId), record);
    },
  });
}
